/**
 * Copyright 2024 Phenix Real Time Solutions, Inc. Confidential and Proprietary. All Rights Reserved.
 */

import moment from 'moment';
import {Dispatch} from 'redux';
import {csvParse} from 'd3';

import store, {IAppStore} from 'store';
import {getConcurrentViewers} from 'services/analytics.service';
import {IPortalError, transformToPortalError} from 'utility/error-handler';

import {ILineChartData} from 'interfaces';
import {mergeSimilarCSVFiles} from 'components/shared/utils';

/**
 * ACTION TYPES
 */
export const CONCURRENTS_FETCHING = 'concurrents::CONCURRENTS_FETCHING' as const;
export const CONCURRENTS_FETCHING_DONE = 'concurrents::CONCURRENTS_FETCHING_DONE' as const;
export const CONCURRENTS_FETCHING_ERROR = 'concurrents::CONCURRENTS_FETCHING_ERROR' as const;

export const SET_CONCURRENTS = 'concurrents::SET_CONCURRENTS' as const;
export const SET_CONCURRENTS_DONE = 'concurrents::SET_CONCURRENTS_DONE' as const;
export const SET_CONCURRENTS_ERROR = 'concurrents::SET_CONCURRENTS_ERROR' as const;

export const CONCURRENTS_SET_CSV = 'concurrents::CONCURRENTS_SET_CSV' as const;
export const CONCURRENTS_SET_QUERY = 'concurrents::CONCURRENTS_SET_QUERY' as const;

/**
 * SELECTORS
 */
export const concurrentsSelector = ({concurrents}: IAppStore): IAppStore['concurrents'] => concurrents;

/**
 * INTERFACES
 */
interface IRequestConcurrents {
  type: typeof CONCURRENTS_FETCHING;
}
interface IReceiveConcurrents {
  type: typeof CONCURRENTS_FETCHING_DONE;
  payload: ILineChartData[];
}
interface IFailedConcurrents {
  type: typeof CONCURRENTS_FETCHING_ERROR;
  error: null | IPortalError;
}

interface IRequestSetConcurrents {
  type: typeof SET_CONCURRENTS;
}
interface IReceiveSetConcurrents {
  type: typeof SET_CONCURRENTS_DONE;
  payload: ILineChartData[];
}
interface IFailedSetConcurrents {
  type: typeof SET_CONCURRENTS_ERROR;
  error: null | Error;
}
interface IConcurrentsSetCSV {
  type: typeof CONCURRENTS_SET_CSV;
  payload: string;
}

interface IConcurrentsSetQuery {
  type: typeof CONCURRENTS_SET_QUERY;
  payload: {start: Date; end: Date};
}

export type FetchConcurrentsActionsType = IRequestConcurrents | IReceiveConcurrents | IFailedConcurrents;
export type SetConcurrentsActionsType = IRequestSetConcurrents | IReceiveSetConcurrents | IFailedSetConcurrents | IConcurrentsSetCSV | IConcurrentsSetQuery;

interface IFetchConcurrentsActions {
  request: () => FetchConcurrentsActionsType;
  receive: (payload) => FetchConcurrentsActionsType;
  failed: (error: null | IPortalError) => FetchConcurrentsActionsType;
}
interface ISetConcurrentsActions {
  request: () => SetConcurrentsActionsType;
  receive: (payload: ILineChartData[]) => SetConcurrentsActionsType;
  failed: (error: null | Error) => SetConcurrentsActionsType;
  setCSV: (payload: string) => SetConcurrentsActionsType;
  setQuery: (payload: {start: Date; end: Date}) => SetConcurrentsActionsType;
}

/**
 * ACTIONS
 */

const fetchConcurrentsActions: IFetchConcurrentsActions = {
  request: () => ({type: CONCURRENTS_FETCHING}),
  receive: payload => ({
    type: CONCURRENTS_FETCHING_DONE,
    payload
  }),
  failed: error => ({
    type: CONCURRENTS_FETCHING_ERROR,
    error
  })
};
const setConcurrentsActions: ISetConcurrentsActions = {
  request: () => ({type: SET_CONCURRENTS}),
  receive: payload => ({
    type: SET_CONCURRENTS_DONE,
    payload
  }),
  failed: error => ({
    type: SET_CONCURRENTS_ERROR,
    error
  }),
  setCSV: payload => ({
    type: CONCURRENTS_SET_CSV,
    payload
  }),
  setQuery: payload => ({
    type: CONCURRENTS_SET_QUERY,
    payload
  })
};

export const getConcurrents = data => async(dispatch: Dispatch<FetchConcurrentsActionsType>): Promise<string> => {
  const {
    request,
    failed
  } = fetchConcurrentsActions;

  dispatch(request());

  try {
    return await getConcurrentViewers(data);
  } catch (e) {
    const portalError = transformToPortalError(e);

    dispatch(failed(portalError));

    throw portalError;
  }
};

export const setConcurrents = (data: string) => async(dispatch: Dispatch<SetConcurrentsActionsType>): Promise<void> => {
  const {
    request,
    receive,
    failed,
    setCSV
  } = setConcurrentsActions;

  dispatch(request());

  try {
    const state = store.getState();
    const {csv} = concurrentsSelector(state);
    const csvFile = csv ? mergeSimilarCSVFiles(data, csv) : data;

    dispatch(setCSV(csvFile));

    const concurrentViewers: ILineChartData[] = csvParse(data)
      .slice(0, -1) // Remove last element
      .map(item => ({
        x: moment.utc(item.Timestamp)?.toDate(),
        y: Number(item.Concurrent)
      }));

    dispatch(receive(concurrentViewers));
  } catch (e) {
    const portalError = transformToPortalError(e);

    dispatch(failed(portalError));

    throw portalError;
  }
};

export const setConcurrentsQuery = (query: {start: Date; end: Date}) => async(dispatch: Dispatch<SetConcurrentsActionsType>): Promise<void> => {
  const {
    request,
    setQuery,
    failed
  } = setConcurrentsActions;

  dispatch(request());

  try {
    dispatch(setQuery(query));
  } catch (e) {
    const portalError = transformToPortalError(e);

    dispatch(failed(portalError));

    throw portalError;
  }
};

export type ConcurrentsActions = FetchConcurrentsActionsType | SetConcurrentsActionsType;